import type { PlanNotice } from "../../lib/api";

/** What a screen reader calls the box. The box itself carries no heading. */
export const PLAN_NOTICES_LABEL = "Notices about your plan";

/**
 * **THE NOTICES THE OWNER PUBLISHED TO THIS PERSON** (ruling R-0923-1 (5)).
 *
 * A boost explained, a complimentary reset announced, or a line on its own —
 * one quiet box, newest first, at the top of the two places usage is shown:
 * the plan screen and the billing panel. No other surface draws it.
 *
 * **THE SERVER DECIDES EVERYTHING ABOUT IT.** Who sees which notice, and until
 * when, arrives on the plan read; the words are the owner's, validated on the
 * server to carry no money figure and no count of work, and drawn here exactly
 * as they were stored. There is no dismissal: a notice stands for its window
 * and is gone when the server stops sending it, which keeps "have I seen this"
 * out of the client entirely.
 *
 * **IT CHANGES NO FIGURE.** While a boost is on, the share beside it is a share
 * of the boosted month — the meter does that — and this box is where the boost
 * is explained. Nothing here reads or adjusts the share.
 *
 * Pure props, like `UsageNotice`: it draws nothing when there is nothing to say.
 */
export function PlanNotices({
  notices,
  className,
}: {
  notices: PlanNotice[] | undefined;
  className?: string;
}) {
  if (!notices || notices.length === 0) return null;
  return (
    <section
      aria-label={PLAN_NOTICES_LABEL}
      data-plan-notices
      className={`rounded-lg border border-border-soft bg-bg px-3 py-2 ${className ?? ""}`}
    >
      <ul className="divide-y divide-border-soft">
        {notices.map((n) => (
          <li
            key={n.id}
            data-plan-notice={n.id}
            className="py-1.5 text-[13px] leading-snug text-text-soft"
          >
            {n.body}
          </li>
        ))}
      </ul>
    </section>
  );
}
